import { KeyboardEvent, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Iconify from 'src/components/iconify';
import { ChatMessage } from 'src/services/ai/ai.types';
import { usePostAiChatMutation } from 'src/services/ai/ai.service';

export default function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const [postChat, { isLoading }] = usePostAiChatMutation();

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    scrollToBottom();

    try {
      const result = await postChat({ messages: next }).unwrap();
      setMessages([...next, { role: 'assistant', content: result.response }]);
      scrollToBottom();
    } catch {
      setMessages([
        ...next,
        { role: 'assistant', content: 'Something went wrong. Please try again.' },
      ]);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating action button */}
      <Fab
        color="primary"
        onClick={() => setOpen((v) => !v)}
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}
      >
        <Iconify icon={open ? 'solar:close-circle-bold' : 'solar:chat-round-bold'} width={24} />
      </Fab>

      {/* Chat panel */}
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 88,
            right: 24,
            width: 380,
            height: 520,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText' }}
          >
            <Stack direction="row" alignItems="center" gap={1}>
              <Iconify icon="solar:stars-bold" width={20} />
              <Typography variant="subtitle2">AI Assistant</Typography>
            </Stack>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
              <Iconify icon="solar:close-circle-bold" width={18} />
            </IconButton>
          </Stack>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {messages.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" mt={4}>
                Ask me anything — &quot;Who should I call today?&quot;, &quot;Draft outreach for a candidate&quot;, or
                &quot;Remind me to follow up on Friday&quot;
              </Typography>
            )}

            {messages.map((msg, i) => (
              <Box
                key={i}
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'background.neutral',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    '& p': { m: 0 },
                    '& ul, & ol': { pl: 2, m: 0 },
                    '& table': { borderCollapse: 'collapse', width: '100%', fontSize: 12 },
                    '& th, & td': { border: '1px solid', p: 0.5 },
                    '& h2, & h3': { mt: 1, mb: 0.5, fontSize: 14 },
                    '& blockquote': { borderLeft: '3px solid', pl: 1, ml: 0, opacity: 0.8 },
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <Typography variant="body2" component="div">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </Typography>
                  ) : (
                    <Typography variant="body2">{msg.content}</Typography>
                  )}
                </Box>
              </Box>
            ))}

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                <Box sx={{ px: 1.5, py: 1, borderRadius: 2, bgcolor: 'background.neutral' }}>
                  <CircularProgress size={16} />
                </Box>
              </Box>
            )}

            <div ref={bottomRef} />
          </Box>

          {/* Input */}
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <InputBase
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask anything..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ fontSize: 14 }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="small"
            >
              <Iconify icon="solar:plain-bold" width={20} />
            </IconButton>
          </Stack>
        </Paper>
      )}
    </>
  );
}
